<script setup>
import Filters from "@/views/orders/Filters/index.vue";
import {onMounted, onUnmounted, ref, watch, computed} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {Icon} from "@iconify/vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {formatPrice} from "@/utils/priceFormatter.js";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import {useRouter} from "vue-router";
import {useOrderFilter} from '@/composables/useOrderFilter';
import UpdateGameAccount from "@/views/stocks/updateGameAccount.vue";
import AddGameAccount from "@/views/stocks/addGameAccount.vue";
import {useUserStore} from "@/stores/user.js";

const router = useRouter();
const ordersStore = useMainOrdersStore();
const userStore = useUserStore();
const isShowUpdateModal = ref(false)
const isShowAddModal = ref(false)
const selectedOrderId = ref(null)
const columns = ref([
  {label: 'ID', field: 'id', thClass: 'custom-head-cell', tdClass: 'custom-cell'},
  {
    label: 'Game',
    field: 'game',
    thClass: 'custom-head-cell',
    tdClass: 'overflow-hidden whitespace-nowrap text-ellipsis max-w-[100px] cursor-pointer custom-cell'
  },
  {
    label: 'Title',
    field: 'title',
    thClass: 'custom-head-cell',
    tdClass: 'overflow-hidden whitespace-nowrap text-ellipsis max-w-[200px] cursor-pointer custom-cell'
  },
  {
    label: 'Username',
    field: 'username',
    thClass: 'custom-head-cell',
    tdClass: 'overflow-hidden whitespace-nowrap text-ellipsis max-w-[100px] cursor-pointer custom-cell'
  },
  {
    label: 'Password',
    field: 'password',
    thClass: 'custom-head-cell',
    tdClass: 'overflow-hidden whitespace-nowrap text-ellipsis max-w-[100px] custom-cell'
  },
  {
    label: 'Last Update',
    field: 'lastUpdate',
    thClass: 'max-w-max custom-head-cell',
    tdClass: 'max-w-max cursor-pointer custom-cell'
  }
])
let interval = null
const search = computed(() => ordersStore.gameAccountsSearch);
const orders = computed(() => ordersStore.fullGameAccounts)

onMounted(() => {
  ordersStore.action_getFullGameAccounts();
})

onUnmounted(() => {
  clearTimeout(interval);
});

async function applyFilters() {
  ordersStore.reset_lastUpdate();
  ordersStore.reset_orders('boostOrders');
  await ordersStore.action_getOrders('Boost')
}

function openUpdateModal(details) {
  if (userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('GAME_ACCOUNTS_UPDATE')) {
    const order = details.row
    selectedOrderId.value = order.id
    isShowUpdateModal.value = true
  }
}

function closeUpdateModal() {
  selectedOrderId.value = null
  isShowUpdateModal.value = false
}

function openAddModal(details) {
  isShowAddModal.value = true
}

function closeAddModal() {
  isShowAddModal.value = false
}

function onSortChange(params) {
  const param = params[0]
  ordersStore.setBoostOrdersSort(param.field, param.type);
}

function refreshList() {
  closeUpdateModal();
  closeAddModal();
  ordersStore.action_getFullGameAccounts();
}

const {filtered: filteredOrders} = useOrderFilter(
    orders,
    search,
    'boost'
);
// وقتی filteredOrders تغییر کرد، مقدار جدید رو در استور ذخیره کن
watch(filteredOrders, (newVal) => {
  ordersStore.boostFilteredOrders = newVal;
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <AllButtons :buttonForNewItem="true" titleForNewItem="New Account" :show-filter="false"
                    :showCategoriesButtons="false" v-model="ordersStore.gameAccountsSearch"
                    @buttonForNewItemClicked="openAddModal" permissionForNewItem="GAME_ACCOUNTS_ADD"
                    :copy-button="false"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <vue-good-table v-else
                      :fixed-header="false"
                      max-height="calc(var(--vh, 1vh) * 100 - 12rem)"
                      :columns="columns"
                      compactMode
                      class="custom-scrollbar-2"
                      styleClass="vgt-table centered lesspadding2 striped ltr text-left custom-scrollbar-2"
                      row-style-class="!text-left normal-case !bg-none hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31]"
                      :rows="filteredOrders"
                      :sort-options="{
                        enabled: true,
                        initialSortBy: ordersStore.boostOrdersSort
                      }"
                      v-on:row-click="openUpdateModal"
                      v-on:sort-change="onSortChange">
        <template v-slot:table-row="props">
          <StateColumn v-if="props.column.field === 'state'" :state="props.row.state"/>
          <div v-if="props.column.field === 'uuid'" @click.stop class="w-full">
            {{ props.row.uuid }}
          </div>
          <div v-if="props.column.field === 'takers'" @click.stop class="w-full">
            {{ props.row.takers }}
          </div>
          <div v-if="props.column.field === 'g2gSellID'" @click.stop class="w-full">
            {{ props.row.g2gSellID }}
          </div>
          <span v-if="props.column.field === 'price'" class="block w-full whitespace-nowrap">
            {{ props.row.price ? formatPrice(props.row.price) + ' $' : '' }}
          </span>
          <span v-if="props.column.field === 'takersCut'" class="block w-full text-red-400 whitespace-nowrap">
            {{ props.row.takersCut ? formatPrice(props.row.takersCut) + ' $' : '' }}
          </span>
          <span v-if="props.column.field === 'revenue'" class="block w-full text-white whitespace-nowrap"
                :class="{ '!text-red-400': props.row.revenue < 0, '!text-success-500': props.row.revenue > 0 }">
            {{ props.row.revenue ? formatPrice(props.row.revenue) + ' $' : '' }}
          </span>
          <span v-if="props.column.field === 'lastUpdate'" class="block w-full max-w-max">
            {{ gregorianDate(props.row.lastUpdate, 'DD MMM HH:mm') }}
          </span>
          <span v-if="props.column.field === 'creationTime'" class="block w-full max-w-max">
            {{ gregorianDate(props.row.creationTime, 'DD MMM YYYY') }}
          </span>
        </template>
      </vue-good-table>
    </div>
    <update-game-account v-if="isShowUpdateModal" :id="selectedOrderId" @closeModal="closeUpdateModal"
                         @deletedAccount="refreshList" @updatedAccount="refreshList"/>
    <add-game-account v-if="isShowAddModal" @closeModal="closeAddModal" @addedNewAccount="refreshList"/>
  </div>
</template>

<style lang="scss">
.custom-head-cell {
  background-image: none !important;
  background-color: #1A2941 !important;
  font-size: .75rem !important;
  white-space: nowrap !important;
  padding: .5rem .5rem .5rem 1rem !important;
  text-transform: capitalize !important;
  text-align: left !important;
}

.custom-cell {
  text-align: left !important;
  padding: .3rem !important;
  font-size: .75rem !important;
}

/**
  Applying `border-collapse: separate` on the table container prevents the first
  table header row's border from being applied elsewhere when it is sticky.
*/
.vgt-table {
  border-collapse: separate !important;
  border-spacing: 0;
  border-top: 0 !important;
}

/**
    Applying `position: sticky` here to make it sticky.
*/
.vgt-table thead {
  position: sticky;
  top: 0
}

/**
    We removed the top border from .vgt-table to apply it here instead
*/
.vgt-table thead tr:first-child th {
  border-top: 1px solid rgb(51, 65, 85) !important;
}

/**
    Applying `border-collapse: separate` doubled the cell border width visually.
    Setting this to a fractional value fixed the issue.
*/
/*.vgt-table th, .vgt-table td {
  border-width: .25px !important
}*/

table.vgt-table td {
  text-transform: none;
}
</style>